import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
    Alert, Box, Button, CircularProgress, Link, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useToast } from '../../../context/ToastContext';
import { AuthService } from '../../../services/auth.service';
import { ManagedDashboardService } from '../../../services/managedDashboard.service';
import {
    BoardAuditRow, ManagedBoard, ManagedPerson, MANAGED_BOARD_KEYS,
} from '../../../interfaces/managedDashboard.interface';
import SearchableSelect from '../../common/SearchableSelect';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { displayUsername } from '../../../utils/userUtils';
import { useDashboardAccess } from '../useDashboardAccess';
import { MODULE_DEFAULT_PRESETS } from '../registry/presets';
import WidgetListEditor, { WidgetListValue } from './WidgetListEditor';

interface Option { id: string; label: string }

const SOURCE_TEXT: Record<string, string> = {
    PERSON: 'their own saved board',
    DEPARTMENT: 'a department default',
    COMPANY: 'the company role default',
    PRESET: 'the built-in board (nothing set yet)',
};

/**
 * One person's board as a list of widget names (People, employee, Dashboard; reached from Team dashboards
 * and from the user list). Layout only: nobody's data is rendered here. The backend re-checks that the
 * person is in the caller's scope on every call.
 */
export default function PersonBoardPage() {
    const { userId = '' } = useParams<{ userId: string }>();
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution() ?? '';
    const { enabledModules } = useDashboardAccess();

    const [boardKey, setBoardKey] = useState('home');
    const [person, setPerson] = useState<ManagedPerson | null>(null);
    const [board, setBoard] = useState<ManagedBoard | null>(null);
    const [value, setValue] = useState<WidgetListValue>({ widgetIds: [], lockedWidgetIds: [] });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);
    const [audit, setAudit] = useState<BoardAuditRow[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Nobody has set this board: seed the editor with the page's built-in widgets so saving never blanks it by accident.
    // The four fixed home pages (and 'home', whose built-in board depends on the person's keys) start from an empty list.
    const apply = useCallback((b: ManagedBoard, key: string) => {
        setBoard(b);
        const seeded = b.source === 'PRESET' && (b.widgetIds ?? []).length === 0
            ? ((MODULE_DEFAULT_PRESETS as Record<string, string[]>)[key] ?? [])
            : (b.widgetIds ?? []);
        setValue({ widgetIds: seeded, lockedWidgetIds: b.lockedWidgetIds ?? [] });
    }, []);

    useEffect(() => {
        if (!institutionId || !userId) return;
        let live = true;
        ManagedDashboardService.people(institutionId)
            .then(p => { if (live) setPerson(p.find(x => x.userId === userId) ?? null); })
            .catch(() => {});
        return () => { live = false; };
    }, [institutionId, userId]);

    const loadAudit = useCallback(() => {
        ManagedDashboardService.boardAudit(institutionId, userId).then(setAudit).catch(() => setAudit([]));
    }, [institutionId, userId]);

    useEffect(() => {
        if (!institutionId || !userId) return;
        let live = true;
        setLoading(true);
        setLoadError(null);
        setBoard(null);
        ManagedDashboardService.getBoard(institutionId, userId, boardKey)
            .then(b => { if (live) apply(b, boardKey); })
            .catch(e => {
                if (!live) return;
                const status = e?.response?.status;
                if (status === 403) setLoadError('You are not allowed to edit this person\'s dashboard.');
                else if (status === 404) setLoadError('This person was not found, or is outside your team.');
                else if (status === 400) setLoadError('This board could not be opened.');
                else toast.showApiError?.(e, 'Could not load this board');
            })
            .finally(() => { if (live) setLoading(false); });
        loadAudit();
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, userId, boardKey, apply, loadAudit]);

    const save = async () => {
        setSaving(true);
        try {
            apply(await ManagedDashboardService.setBoard(institutionId, userId, boardKey, value), boardKey);
            toast.showSuccess?.('Board saved');
            loadAudit();
        } catch (e) {
            toast.showApiError?.(e, 'Could not save this board');
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        try {
            apply(await ManagedDashboardService.clearBoard(institutionId, userId, boardKey), boardKey);
            toast.showSuccess?.('Board reset to the role default');
            setConfirmReset(false);
            loadAudit();
        } catch (e) {
            toast.showApiError?.(e, 'Could not reset this board');
        }
    };

    const boardOptions = useMemo<Option[]>(() => MANAGED_BOARD_KEYS.map(b => ({ id: b.key, label: b.label })), []);
    const name = person ? (person.name?.trim() || displayUsername(person.username)) : 'this person';

    return (
        <Box sx={{ p: 2, minWidth: 0 }} data-help-page="dashboard.person">
            <Typography variant="h5" fontWeight={700} gutterBottom>Dashboard: {name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-help-section="dashboard.person.intro">
                Add, remove, reorder or lock the widgets on {name}&apos;s board. They can still add and remove anything that is not locked.
                A widget only shows for them if their roles allow it. You see widget names, never their data.{' '}
                <Link data-help="dashboard.person.back" component={RouterLink} to="/dashboard/team">Back to team dashboards</Link>
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box data-help="dashboard.person.board" sx={{ flex: '1 1 240px', maxWidth: 360 }}>
                    <SearchableSelect<Option> label="Board" size="small" clearable={false} options={boardOptions} value={boardKey}
                        getOptionLabel={o => o.label} onChange={id => id && setBoardKey(id)} data-testid="board-select" />
                </Box>
            </Box>

            {loadError && <Alert severity="error" sx={{ mb: 2 }} data-testid="board-load-error" data-help-section="dashboard.person.loadError">{loadError}</Alert>}
            {loading ? <CircularProgress size={24} /> : !loadError && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }} data-help-section="dashboard.person.editor">
                    {board && (
                        <Alert severity="info" sx={{ mb: 2 }} data-testid="board-source" data-help-section="dashboard.person.source">
                            Showing {SOURCE_TEXT[board.source] ?? board.source}
                            {board.setByKind && board.setByKind !== 'SELF' ? `, set by ${board.setByKind.toLowerCase().replace('_', ' ')}` : ''}.
                        </Alert>
                    )}
                    {board?.source === 'PRESET' && boardKey === 'home' && (
                        <Alert severity="warning" sx={{ mb: 2 }} data-testid="home-preset-note" data-help-section="dashboard.person.presetNote">
                            Nothing is set for this board yet, so {name} currently sees the built-in board, which depends on their roles.
                            The list below starts empty: saving it replaces the built-in board with exactly the widgets you add.
                        </Alert>
                    )}
                    <WidgetListEditor value={value} onChange={setValue} enabledModules={enabledModules} />
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                        <Button data-help="dashboard.person.save" variant="contained" onClick={save} disabled={saving}>Save board</Button>
                        <Button data-help="dashboard.person.reset" onClick={() => setConfirmReset(true)} disabled={saving}>Reset to role default</Button>
                    </Stack>
                </Paper>
            )}

            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Change history</Typography>
            <Paper variant="outlined" sx={{ overflowX: 'auto' }} data-help-section="dashboard.person.history">
                <Table size="small">
                    <TableHead>
                        <TableRow><TableCell data-help="dashboard.person.col.when">When</TableCell><TableCell data-help="dashboard.person.col.by">By</TableCell><TableCell data-help="dashboard.person.col.action">Action</TableCell><TableCell data-help="dashboard.person.col.board">Board</TableCell><TableCell data-help="dashboard.person.col.widgets">Widgets</TableCell></TableRow>
                    </TableHead>
                    <TableBody>
                        {audit.length === 0 && <TableRow><TableCell colSpan={5}>No changes by others yet.</TableCell></TableRow>}
                        {audit.map(a => (
                            <TableRow key={a.id}>
                                <TableCell>{new Date(a.at).toLocaleString()}</TableCell>
                                <TableCell>{a.actorKind}</TableCell>
                                <TableCell>{a.action}</TableCell>
                                <TableCell>{a.boardKey}</TableCell>
                                <TableCell>{(a.before ?? []).length} to {(a.after ?? []).length}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <ConfirmDialog
                open={confirmReset}
                title="Reset to role default?"
                message={`${name}'s own changes to this board are removed and the role or department default applies again.`}
                confirmLabel="Reset"
                onCancel={() => setConfirmReset(false)}
                onConfirm={reset}
            />
        </Box>
    );
}
